import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ExternalLink, Trash2, X, Lock, RotateCw, Plus,
} from 'lucide-react';
import axiosInstance from 'shared/services/api/axiosInstance';
import { useTeam } from 'shared/context/teamContext';
import { useAppDialog } from 'shared/context/AppDialogContext';
import { settingsCardShell } from 'features/settings/components/settingsTheme';

// ─── Shared theme-aware class tokens (light + dark via CSS vars) ──────────────
const BORDER    = 'border-[color:var(--border-default)]';
const PANEL     = `rounded-lg border ${BORDER} bg-[color:var(--surface-raised)] shadow-2xl`;
const TXT       = 'text-[color:var(--text-primary)]';
const MUTED     = 'text-[color:var(--text-muted)]';
const BTN_BRAND = 'bg-brand text-ink-onBrand hover:bg-brand-hover disabled:opacity-50';
const BTN_GHOST = `border ${BORDER} bg-[color:var(--surface-base)] ${TXT} hover:bg-[color:var(--surface-hover)]`;

/** Short, locale-aware date — "Jul 14, 2026" — or an em dash when absent. */
function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Index-status → dot colour (mirrors the run/span status dots). */
function statusColor(status) {
  switch (status) {
    case 'ready':    return 'var(--feedback-success)';
    case 'indexing':
    case 'pending':  return 'var(--feedback-warning)';
    case 'failed':   return 'var(--feedback-error)';
    default:         return 'var(--text-disabled)'; // stale / unknown
  }
}

/**
 * GitHub integration — table-style page.
 *
 * Flow:
 *   1. Install the Trodo GitHub App once (grants access to selected repos).
 *   2. "Add repo" sends the user back to GitHub to grant access to more repos;
 *      each accessible repo is auto-linked as its own record on the callback.
 *   3. Each repo can have agents linked to it (one agent → one repo). Issue
 *      detection uses this to pick which repo to inspect for a given agent.
 */
export default function GithubIntegrationPage({ teamId: teamIdProp, onHeaderChange } = {}) {
  const { selectedTeam } = useTeam() || {};
  const teamId = teamIdProp || selectedTeam?.id || selectedTeam?._id || null;
  const location = useLocation();
  const navigate = useNavigate();
  const dialog = useAppDialog();

  const [loading, setLoading] = useState(true);  // only gates the FIRST render
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [integration, setIntegration] = useState(null);
  const [linkedRepos, setLinkedRepos] = useState([]);
  const [teamAgents, setTeamAgents] = useState([]);
  const [agentsByRepoId, setAgentsByRepoId] = useState({});  // repo_id → [agent_name]
  const [agentToRepo, setAgentToRepo] = useState({});        // agent_name → { repo_id, owner, repo_name }
  const [agentsRepo, setAgentsRepo] = useState(null);        // repo whose agents modal is open

  const load = useCallback(async () => {
    if (!teamId) { setLoading(false); return; }
    setError(null);
    try {
      // Agent picker must use /agents/available (DISTINCT agent_name from
      // agent_runs), NOT /agents (team_agents settings rows). Runs/spans can
      // exist while team_agents is empty — that previously showed "No agents".
      const [intRes, reposRes, agentsRes, mapRes] = await Promise.all([
        axiosInstance.get(`/integrations/github`, { params: { team_id: teamId } })
          .catch(() => ({ data: { integration: null } })),
        axiosInstance.get(`/integrations/github/repos`, { params: { team_id: teamId } })
          .catch(() => ({ data: { items: [] } })),
        axiosInstance.get(`/team/${teamId}/agents/available`)
          .catch(() => ({ data: { agents: [] } })),
        axiosInstance.get(`/integrations/github/agent-mappings`, { params: { team_id: teamId } })
          .catch(() => ({ data: { items: [] } })),
      ]);
      setIntegration(intRes.data?.integration || null);
      setLinkedRepos(reposRes.data?.items || []);
      const available = agentsRes.data?.agents || [];
      // Normalize string[] from /available into { agent_name } objects the modal expects.
      setTeamAgents(
        available.map((a) => (typeof a === 'string' ? { agent_name: a } : a)),
      );

      // Build both directions from the flat mapping list.
      const byRepo = {};
      const byAgent = {};
      for (const m of (mapRes.data?.items || [])) {
        (byRepo[m.repo_id] = byRepo[m.repo_id] || []).push(m.agent_name);
        byAgent[m.agent_name] = { repo_id: m.repo_id, owner: m.owner, repo_name: m.repo_name };
      }
      setAgentsByRepoId(byRepo);
      setAgentToRepo(byAgent);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  // Redirect to GitHub's install/configure page. Used for both first-time
  // connect and "Add repo" — GitHub routes an existing installation straight to
  // its configure screen. The callback auto-links whatever the user selects.
  const openGithubInstall = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await axiosInstance.get('/integrations/github/install-url', {
        params: { team_id: teamId },
      });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      const body = e?.response?.data;
      setError(body?.message || body?.error || e.message);
    } finally {
      setBusy(false);
    }
  }, [teamId]);

  // After returning from a GitHub install (callback appends ?github_ok=1) the
  // backend has already auto-linked every repo, so just refresh. Strip the param.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('github_ok') === '1') {
      load();
      params.delete('github_ok');
      navigate(`${location.pathname}?${params.toString()}`.replace(/\?$/, ''), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Hoist the "Add repo" action to whoever renders this page — currently the
  // Settings > Project > Integrations panel, which puts it beside the back link.
  useEffect(() => {
    onHeaderChange?.(integration ? { onAddRepo: openGithubInstall, busy } : null);
  }, [integration, busy, openGithubInstall, onHeaderChange]);
  useEffect(() => () => onHeaderChange?.(null), [onHeaderChange]);

  async function unlinkRepo(repo) {
    const ok = await dialog.confirm(
      `Heal will stop opening pull requests against ${repo.owner}/${repo.name}, and issue analysis will lose access to its code.`,
      { title: 'Unlink this repository?', confirmLabel: 'Unlink', cancelLabel: 'Cancel', danger: true },
    );
    if (!ok) return;
    try {
      await axiosInstance.delete(`/integrations/github/repos/${repo.id}`, { params: { team_id: teamId } });
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    }
  }

  // Re-queue a failed repo's index job. The backend flips it back to "pending"
  // so the dot turns amber immediately, then the worker retries.
  async function retryIndex(repo) {
    try {
      await axiosInstance.post(`/integrations/github/repos/${repo.id}/reindex`, {}, { params: { team_id: teamId } });
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    }
  }

  if (loading) {
    return <div className={`px-8 py-6 text-sm ${MUTED}`}>Loading…</div>;
  }

  const cols = 'grid-cols-[2.2fr_1.6fr_1fr]';

  return (
    <div className="px-8 py-6">
      {error && (
        <div className={`mb-4 rounded-md border ${BORDER} bg-[color:var(--feedback-warningBg)] px-3 py-2 text-[12px] text-[color:var(--feedback-warning)]`}>
          {String(error)}
        </div>
      )}

      {/* Connect (only when no integration) */}
      {!integration && (
        <div className={`${settingsCardShell} p-5`}>
          <p className={`mb-3 text-[13px] ${MUTED}`}>
            Connect your repositories so Trodo can locate failing code, open fix PRs, and ground issue analysis in your codebase.
          </p>
          <button
            onClick={openGithubInstall}
            disabled={busy}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium ${BTN_BRAND}`}
          >
            <ExternalLink size={14} /> Install Trodo on GitHub
          </button>
        </div>
      )}

      {/* Linked repos table */}
      {integration && (
        <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
          <div className={`grid h-8 ${cols} items-center gap-3 border-b border-[color:var(--border-default)] px-3 text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap text-[color:var(--text-muted)]`}>
            <span>Repository</span>
            <span>Agents</span>
            <span>Linked</span>
          </div>
          {linkedRepos.length === 0 ? (
            <div className={`px-4 py-8 text-center text-[12px] ${MUTED}`}>No repositories linked</div>
          ) : (
            linkedRepos.map((r) => {
              const agents = agentsByRepoId[r.id] || [];
              return (
                <div
                  key={r.id}
                  className={`group relative grid h-8 ${cols} items-center gap-3 border-b border-[color:var(--border-subtle)] px-3 text-[12px] text-[color:var(--text-primary)] last:border-b-0 hover:bg-surface-hover transition-colors`}
                >
                  <span className="truncate flex items-center gap-2.5">
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: statusColor(r.index_status) }}
                      title={r.index_status}
                    />
                    <span className="truncate font-medium">{r.owner}/{r.name}</span>
                    {r.is_private && (
                      <span className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide bg-[color:var(--surface-sunken)] ${MUTED}`}>
                        <Lock size={9} /> private
                      </span>
                    )}
                  </span>
                  <span className="flex min-w-0 items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setAgentsRepo(r)}
                      title="Link agents"
                      className="hidden shrink-0 rounded p-0.5 text-[color:var(--text-muted)] hover:bg-[color:var(--surface-base)] hover:text-[color:var(--text-primary)] group-hover:inline-flex"
                    >
                      <Plus size={13} />
                    </button>
                    <span className={`truncate text-[12px] text-[color:var(--text-secondary)]${agents.length === 0 ? ' group-hover:hidden' : ''}`}>
                      {agents.length === 0
                        ? <span className="text-[color:var(--text-disabled)] italic">none</span>
                        : agents.join(', ')}
                    </span>
                  </span>
                  <span className="text-[12px] text-[color:var(--text-secondary)]">{fmtDate(r.linked_at)}</span>

                  {/* Row actions: revealed on hover, floated over the right edge */}
                  <span className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1 rounded-md bg-[color:var(--surface-hover)] pl-3 opacity-0 transition-opacity group-hover:opacity-100">
                    {r.index_status === 'failed' && (
                      <button
                        type="button"
                        onClick={() => retryIndex(r)}
                        className={`inline-flex items-center justify-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium ${BTN_GHOST}`}
                      >
                        <RotateCw size={11} /> Retry
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => unlinkRepo(r)}
                      className="inline-flex items-center justify-center gap-1 rounded-md border border-[color:var(--feedback-error)] px-2 py-1 text-[11px] font-medium text-[color:var(--feedback-error)] hover:bg-[color:var(--feedback-errorBg)]"
                    >
                      <Trash2 size={11} /> Unlink
                    </button>
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Agent-linking popup */}
      {agentsRepo && (
        <LinkAgentsModal
          teamId={teamId}
          repo={agentsRepo}
          teamAgents={teamAgents}
          agentToRepo={agentToRepo}
          onClose={() => setAgentsRepo(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

// ─── Link Agents Modal ──────────────────────────────────────────────────────

/**
 * Link team agents to a repo — one agent may only be linked to ONE repo. Agents
 * already linked elsewhere are shown disabled with where they're linked, so the
 * user knows to unlink there first.
 */
function LinkAgentsModal({ teamId, repo, teamAgents, agentToRepo, onClose, onChanged }) {
  const [busyAgent, setBusyAgent] = useState(null);
  const [err, setErr] = useState(null);

  // /agents/available returns plain agent-name strings (sourced from
  // agent_runs); older shapes returned row objects. Accept both.
  const agentNames = [...new Set(
    (teamAgents || [])
      .map((a) => (typeof a === 'string' ? a : a?.agent_name || a?.name))
      .filter(Boolean)
  )];

  async function toggle(name, linkedHere) {
    setBusyAgent(name);
    setErr(null);
    try {
      if (linkedHere) {
        await axiosInstance.delete(
          `/integrations/github/repos/${repo.id}/agents/${encodeURIComponent(name)}`,
          { params: { team_id: teamId } });
      } else {
        await axiosInstance.post(
          `/integrations/github/repos/${repo.id}/agents`,
          { agent_name: name }, { params: { team_id: teamId } });
      }
      await onChanged();
    } catch (e) {
      setErr(e?.response?.data?.error === 'agent_taken'
        ? 'That agent is already linked to another repo: unlink it there first.'
        : (e?.response?.data?.error || e.message));
    } finally {
      setBusyAgent(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-surface-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`w-full max-w-md ${PANEL}`}>
        <header className={`flex items-center justify-between border-b ${BORDER} px-4 py-3`}>
          <div className="min-w-0">
            <p className={`text-[10px] uppercase tracking-wide ${MUTED}`}>Link agents</p>
            <p className={`truncate text-sm font-semibold ${TXT}`}>{repo.owner}/{repo.name}</p>
          </div>
          <button onClick={onClose} className={`rounded p-1 ${MUTED} hover:bg-[color:var(--surface-hover)]`}>
            <X size={14} />
          </button>
        </header>

        <div className="px-4 py-4 space-y-3">
          {agentNames.length === 0 ? (
            <p className={`rounded border ${BORDER} px-3 py-6 text-center text-[12px] ${MUTED}`}>
              No agents detected for this team yet.
            </p>
          ) : (
            <div className={`max-h-72 overflow-y-auto rounded border ${BORDER}`}>
              {agentNames.map((name) => {
                const info = agentToRepo[name];
                const linkedHere = info && Number(info.repo_id) === Number(repo.id);
                const linkedElsewhere = info && !linkedHere;
                const disabled = linkedElsewhere || busyAgent === name;
                return (
                  <label
                    key={name}
                    className={`flex items-center gap-2.5 border-b ${BORDER} px-3 py-2.5 text-[13px] last:border-0 ${
                      disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-[color:var(--surface-hover)]'
                    } ${TXT}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!linkedHere}
                      disabled={disabled}
                      onChange={() => toggle(name, linkedHere)}
                      style={{ accentColor: 'var(--brand-primary)' }}
                    />
                    <span className="truncate">{name}</span>
                    {linkedElsewhere && (
                      <span className={`ml-auto shrink-0 text-[11px] ${MUTED}`}>
                        linked to {info.owner}/{info.repo_name}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          )}

          {err && (
            <div className={`rounded-md border ${BORDER} bg-[color:var(--feedback-warningBg)] px-3 py-2 text-[12px] text-[color:var(--feedback-warning)]`}>
              {err}
            </div>
          )}
        </div>

        <footer className={`flex items-center justify-end border-t ${BORDER} px-4 py-3`}>
          <button
            onClick={onClose}
            className={`rounded-md px-3 py-1.5 text-[12px] font-medium ${BTN_GHOST}`}
          >
            Done
          </button>
        </footer>
      </div>
    </div>
  );
}
